import { Fragment } from "react";

import { Icon } from "../../../components/icons";
import { MOCK_CATEGORIES, MOCK_CLUB, MOCK_ELIM_PLAYERS, MOCK_GROUP_A, MOCK_LADDER, MOCK_TOURNAMENT } from "../mockData";
import { Avatar, MockFigure, PanelFrame } from "./frames";
import "./CloseRankingMock.css";

/**
 * M10 · Cierre del torneo y ranking del club. «Cerrado · solo lectura»,
 * Categoría / Campeón / Subcampeón y «Redactar comunicado de campeones» son
 * textos reales (AdminTournamentHomePage y TournamentLifecycleCard).
 */

const P = MOCK_ELIM_PLAYERS;
const [ortiz, mendez] = MOCK_GROUP_A.map((player) => player.name);
const [fuentes, robles] = MOCK_LADDER.map((player) => player.name);
const [varonil, femenil, mixto] = MOCK_CATEGORIES;

/** Campeón y subcampeón: uno o dos nombres (dobles, uno por renglón). */
const PODIUM: { category: string; champion: string[]; runnerUp: string[] }[] = [
  { category: varonil, champion: [P.salazar], runnerUp: [P.ruiz] },
  { category: femenil, champion: [ortiz], runnerUp: [mendez] },
  { category: mixto, champion: [fuentes, P.aguilar], runnerUp: [robles, P.torres] },
];

/** Mismas columnas que el ranking público (PublicRankingPage): lugar,
 * jugador, puntos y torneos que cuentan. El ranking no guarda cuántos
 * lugares sube o baja cada jugador. */
const RANKING: { name: string; points: number; tournaments: number }[] = [
  { name: P.salazar, points: 1250, tournaments: 5 },
  { name: P.ruiz, points: 1100, tournaments: 5 },
  { name: P.herrera, points: 980, tournaments: 4 },
  { name: P.navarro, points: 860, tournaments: 4 },
  { name: P.castro, points: 610, tournaments: 2 },
];

const points = new Intl.NumberFormat("es-MX");

export function CloseRankingMock({ className = "" }: { className?: string }) {
  return (
    <MockFigure
      className={`mk-close ${className}`.trim()}
      label="Resumen de la Copa Otoño 2026 cerrada en solo lectura, con campeón y subcampeón de cada categoría y el botón para redactar el comunicado de campeones; debajo, el ranking del Club Arboleda en Varonil Abierta B con el lugar, los puntos y los torneos de cada jugador, y el enlace público para compartirlo."
    >
      <div className="mk-close__stage">
        <PanelFrame
          title={MOCK_TOURNAMENT.name}
          className="mk-close__summary"
          badge={
            <>
              <Icon name="lock" />
              Cerrado · solo lectura
            </>
          }
        >
          <span className="mk-close__eyebrow">Torneo cerrado</span>
          <p className="mk-close__note">Resultados, cuadros, calendario y ranking ya no cambian.</p>

          <div className="mk-close__table">
            <div className="mk-close__tr mk-close__tr--head">
              <span>Categoría</span>
              <span>Campeón</span>
              <span>Subcampeón</span>
            </div>
            {PODIUM.map((row) => (
              <div key={row.category} className="mk-close__tr">
                <span className="mk-close__cat">{row.category}</span>
                <span className="mk-close__champ">
                  <Icon name="trophy" />
                  <Names names={row.champion} />
                </span>
                <span className="mk-close__runner">
                  <Names names={row.runnerUp} />
                </span>
              </div>
            ))}
          </div>

          <span className="mk-close__cta">
            <Icon name="megaphone" />
            Redactar comunicado de campeones
          </span>
        </PanelFrame>

        <div className="mk-close__rank">
          <div className="mk-close__rank-head">
            <span className="mk-close__rank-icon">
              <Icon name="award" />
            </span>
            <span className="mk-close__rank-title">
              <strong>Ranking {MOCK_CLUB.name} 2026</strong>
              <small>{varonil}</small>
            </span>
          </div>

          <div className="mk-close__list-head">
            <span>#</span>
            <span>Jugador</span>
            <span>Puntos</span>
            <span>Torneos</span>
          </div>
          <ol className="mk-close__list">
            {RANKING.map((row, index) => (
              <li key={row.name} className={index === 0 ? "is-top" : undefined}>
                <span className="mk-close__pos">{index + 1}</span>
                <Avatar name={row.name} className="mk-close__avatar" />
                <span className="mk-close__name">{row.name}</span>
                <span className="mk-close__pts">
                  {points.format(row.points)}
                  <small> pts</small>
                </span>
                <span className="mk-close__tourn">{row.tournaments}</span>
              </li>
            ))}
          </ol>

          <div className="mk-close__share">
            <LinkGlyph />
            <span>Enlace público del ranking</span>
            <span className="mk-close__copy">Copiar</span>
          </div>
        </div>
      </div>
    </MockFigure>
  );
}

function Names({ names }: { names: string[] }) {
  return (
    <span className="mk-close__names">
      {names.map((name, index) => (
        <Fragment key={name}>
          {index > 0 ? <span className="mk-close__sep"> / </span> : null}
          <span>{name}</span>
        </Fragment>
      ))}
    </span>
  );
}

function LinkGlyph() {
  return (
    <svg className="mk-close__link" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M10 13.5a4.5 4.5 0 0 0 6.4.4l2.8-2.8a4.5 4.5 0 0 0-6.4-6.4l-1.2 1.2" />
      <path d="M14 10.5a4.5 4.5 0 0 0-6.4-.4l-2.8 2.8a4.5 4.5 0 0 0 6.4 6.4l1.2-1.2" />
    </svg>
  );
}
